// Admin page tab for the dashboard banner's featured slides: order, switch
// on/off, schedule, add, edit and delete. Rows live in the SharePoint
// "Featured Content" list.
import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Film, Image as ImageIcon, Loader2, Pencil, Plus, Trash2, Type, UploadCloud } from "lucide-react";
import { useGraphToken } from "../auth/useGraphToken";
import {
  deleteFeaturedRow,
  fetchFeaturedRows,
  previewImage,
  saveFeaturedOrder,
  saveFeaturedRow,
  setFeaturedActive,
  slideStatus,
  uploadFeaturedFile,
  ymd,
  type FeaturedRow,
  type SlideStatus,
} from "../api/featured";
import { FEATURED_SLIDES } from "../data/featuredContent";
import { BannerSlideEditor } from "./BannerSlideEditor";
import { ConfirmDialog } from "./ConfirmDialog";

const shortDate = (d: string) => new Date(`${d}T00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

function statusLabel(row: FeaturedRow, status: SlideStatus): string {
  if (status === "scheduled") return `Starts ${shortDate(row.startDate)}`;
  if (status === "ended") return `Ended ${shortDate(row.endDate)}`;
  if (status === "off") return "Off";
  return row.endDate ? `Live until ${shortDate(row.endDate)}` : "Live";
}

const MEDIA_ICON = { Video: Film, Image: ImageIcon, None: Type };

export function BannerAdmin() {
  const getToken = useGraphToken();
  const [rows, setRows] = useState<FeaturedRow[] | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string | undefined>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<FeaturedRow | "new" | null>(null);
  const [deleting, setDeleting] = useState<FeaturedRow | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await fetchFeaturedRows(await getToken()));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setRows((r) => r ?? []);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  // Thumbnails load after the list so it shows straight away.
  useEffect(() => {
    if (!rows?.length) return;
    let alive = true;
    void (async () => {
      const token = await getToken();
      for (const row of rows) {
        const url = await previewImage(token, row).catch(() => undefined);
        if (!alive) return;
        setThumbs((t) => ({ ...t, [row.id]: url }));
      }
    })();
    return () => {
      alive = false;
    };
  }, [rows, getToken]);

  const run = async (label: string, job: (token: string) => Promise<void>) => {
    setBusy(label);
    setError(null);
    try {
      await job(await getToken());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await load();
    } finally {
      setBusy(null);
    }
  };

  const move = (index: number, by: -1 | 1) => {
    if (!rows) return;
    const next = [...rows];
    const [row] = next.splice(index, 1);
    next.splice(index + by, 0, row);
    setRows(next);
    void run("Saving order", async (token) => {
      await saveFeaturedOrder(token, next);
      setRows(next.map((r, i) => ({ ...r, sortOrder: i + 1 })));
    });
  };

  const toggle = (row: FeaturedRow) => {
    setRows((rs) => rs?.map((r) => (r.id === row.id ? { ...r, active: !r.active } : r)) ?? rs);
    void run("Saving", (token) => setFeaturedActive(token, row, !row.active));
  };

  const remove = () => {
    const row = deleting;
    if (!row) return;
    void run("Deleting", async (token) => {
      await deleteFeaturedRow(token, row);
      setDeleting(null);
      await load();
    });
  };

  // Moves the slides that ship with the app into SharePoint, files included.
  const importBuiltIn = () =>
    run("Importing", async (token) => {
      for (const [i, s] of FEATURED_SLIDES.entries()) {
        const upload = async (path?: string) => {
          if (!path?.startsWith("/")) return path ?? "";
          const blob = await (await fetch(path)).blob();
          return uploadFeaturedFile(token, blob, path.split("/").pop() || "file");
        };
        await saveFeaturedRow(token, {
          title: s.title,
          description: s.description ?? "",
          tag: s.tag ?? "",
          meta: s.meta ?? "",
          mediaType: s.videoUrl ? "Video" : s.image ? "Image" : "None",
          mediaUrl: await upload(s.videoUrl ?? s.image),
          posterUrl: s.videoUrl ? await upload(s.image) : "",
          buttonLabel: s.link?.label ?? "",
          buttonUrl: s.link?.url ?? "",
          sortOrder: i + 1,
          active: true,
          startDate: "",
          endDate: "",
        });
      }
      await load();
    });

  const today = ymd(new Date());
  const liveCount = rows?.filter((r) => slideStatus(r, today) === "live").length ?? 0;
  const nextSortOrder = (rows ?? []).reduce((m, r) => Math.max(m, r.sortOrder), 0) + 1;

  return (
    <div className="bn-admin">
      <div className="bn-admin-head">
        <div>
          <h2 className="bn-admin-title">Featured slides</h2>
          <p className="bn-admin-sub">
            {rows == null
              ? "Loading…"
              : rows.length
                ? `${rows.length} slide${rows.length === 1 ? "" : "s"} · ${liveCount} live now. They rotate on the banner in this order, and changes show the next time someone opens the dashboard.`
                : "The banner is showing the built-in Scout demo until slides are added here."}
          </p>
        </div>
        <div className="bn-admin-actions">
          {busy ? (
            <span className="bn-note" role="status">
              <Loader2 size={14} className="add-item-spin" aria-hidden /> {busy}…
            </span>
          ) : null}
          <button type="button" className="btn btn-primary" onClick={() => setEditing("new")} disabled={rows == null}>
            <Plus size={15} aria-hidden /> Add slide
          </button>
        </div>
      </div>

      {error ? (
        <div className="error-banner" role="alert">
          <strong>Something went wrong.</strong> {error}
        </div>
      ) : null}

      {rows == null ? (
        <div className="bn-skel" aria-busy="true">
          <span />
          <span />
        </div>
      ) : rows.length === 0 ? (
        <div className="bn-empty">
          <UploadCloud size={26} aria-hidden />
          <p>
            <strong>No slides in SharePoint yet.</strong> Add your first one, or move the built-in Scout demo into
            SharePoint so you can manage it here.
          </p>
          <button type="button" className="btn" onClick={() => void importBuiltIn()} disabled={Boolean(busy)}>
            Import the Scout demo
          </button>
        </div>
      ) : (
        <ol className="bn-list">
          {rows.map((row, i) => {
            const status = slideStatus(row, today);
            const Icon = MEDIA_ICON[row.mediaType];
            return (
              <li key={row.id} className={`bn-row${status === "live" ? "" : " is-muted"}`}>
                <span className="bn-pos">{i + 1}</span>
                <span className="bn-thumb">
                  {thumbs[row.id] ? <img src={thumbs[row.id]} alt="" /> : <Icon size={18} aria-hidden />}
                </span>
                <span className="bn-row-body">
                  <span className="bn-row-title">{row.title || "Untitled"}</span>
                  <span className="bn-row-meta">
                    {[row.mediaType === "None" ? "Text only" : row.mediaType, row.tag, row.meta].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <span className={`bn-status bn-status--${status}`}>{statusLabel(row, status)}</span>
                <span className="bn-row-actions">
                  <label className="bn-switch bn-switch--compact" title={row.active ? "Turn off" : "Turn on"}>
                    <input
                      type="checkbox"
                      checked={row.active}
                      onChange={() => toggle(row)}
                      disabled={Boolean(busy)}
                      aria-label={`${row.title}: ${row.active ? "on" : "off"}`}
                    />
                    <span className="bn-switch-track" aria-hidden />
                  </label>
                  <button
                    type="button"
                    className="bn-icon-btn"
                    onClick={() => move(i, -1)}
                    disabled={i === 0 || Boolean(busy)}
                    aria-label={`Move ${row.title} up`}
                  >
                    <ArrowUp size={15} />
                  </button>
                  <button
                    type="button"
                    className="bn-icon-btn"
                    onClick={() => move(i, 1)}
                    disabled={i === rows.length - 1 || Boolean(busy)}
                    aria-label={`Move ${row.title} down`}
                  >
                    <ArrowDown size={15} />
                  </button>
                  <button type="button" className="bn-icon-btn" onClick={() => setEditing(row)} aria-label={`Edit ${row.title}`}>
                    <Pencil size={15} />
                  </button>
                  <button
                    type="button"
                    className="bn-icon-btn bn-icon-btn--danger"
                    onClick={() => setDeleting(row)}
                    disabled={Boolean(busy)}
                    aria-label={`Delete ${row.title}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {editing ? (
        <BannerSlideEditor
          row={editing === "new" ? null : editing}
          nextSortOrder={nextSortOrder}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete slide?"
        message={
          <p>
            <strong>{deleting?.title || "Untitled"}</strong> will be removed from the banner. Its files stay in Shared
            Documents → Featured Content.
          </p>
        }
        confirmLabel="Delete"
        tone="danger"
        busy={busy === "Deleting"}
        onConfirm={remove}
        onCancel={() => (busy ? null : setDeleting(null))}
      />
    </div>
  );
}
